import { Inject, Injectable } from '@angular/core';
import {
    AppNotificationService,
    BaseInitializationService,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigInstanceToken,
    ConfigInterface,
    ConfigRequestsInstanceToken,
    ConfigRequestsInterface,
    ConnectivityService,
    EncryptionInitializer,
    EncryptionMigrator,
    EventService,
    FileStorageInjectionToken,
    FileStorageServiceInterface,
    INIT_MESSAGE_KEY,
    INIT_MESSAGE_STATE,
    InitError,
    InitMessage,
    LocalStorageAppStateService,
    LocalStorageCommonService,
    SideMenuInstanceToken,
    SideMenuInterface,
    UriUtilitiesService,
    UserGidService,
    UserMasterInstanceToken,
    UserMasterInterface,
    ValueHelperInstanceToken,
    ValueHelperInterface,
} from '@evorait/pace-core';
import { FormsService, MeasurementToolsService } from '@evorait/pace-forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { OfflineService } from '@evorait/pace-offline';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, mergeMap, tap } from 'rxjs/operators';
import { SynchronizationService } from '../synchronization/synchronization.service';

@Injectable({
    providedIn: 'root',
})
export class InitializationService extends BaseInitializationService {
    public constructor(
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        @Inject(ClientManagerInstanceToken) protected readonly clientManager: ClientManagerInterface,
        @Inject(SideMenuInstanceToken) protected readonly sideMenuService: SideMenuInterface,
        @Inject(ConfigRequestsInstanceToken) protected readonly configInterfaceService: ConfigRequestsInterface,
        protected readonly modalService: BsModalService,
        protected readonly localStorageAppStateService: LocalStorageAppStateService,
        protected readonly connectivityService: ConnectivityService,
        protected readonly uriUtilities: UriUtilitiesService,
        @Inject(UserMasterInstanceToken) protected readonly userMasterService: UserMasterInterface,
        protected readonly appNotificationService: AppNotificationService,
        protected readonly translate: TranslateService,
        protected readonly eventService: EventService,
        protected readonly synchronizationService: SynchronizationService,
        @Inject(ConfigInstanceToken) protected readonly configService: ConfigInterface,
        protected readonly offlineService: OfflineService,
        protected readonly userGidService: UserGidService,
        @Inject(ValueHelperInstanceToken) private readonly valueHelperService: ValueHelperInterface,
        protected readonly encryptionInitializer: EncryptionInitializer,
        protected readonly localStorageCommonService: LocalStorageCommonService,
        protected readonly formsService: FormsService,
        private readonly measurementToolsService: MeasurementToolsService,
        @Inject(FileStorageInjectionToken) protected readonly fileStorageServiceInterface: FileStorageServiceInterface,
        protected readonly encryptionMigrator: EncryptionMigrator,
    ) {
        super(
            log,
            clientManager,
            sideMenuService,
            configInterfaceService,
            modalService,
            localStorageAppStateService,
            connectivityService,
            uriUtilities,
            userMasterService,
            appNotificationService,
            translate,
            eventService,
            synchronizationService,
            configService,
            offlineService,
            userGidService,
            localStorageCommonService,
            fileStorageServiceInterface,
            encryptionMigrator,
            encryptionInitializer,
        );
    }

    protected setupInitProcessPipe(): void {
        // initialize empty observable as starting point
        this.init$ = of(undefined);
        this.init$ = this.attachEncryptionServiceInit(this.init$); // INIT_MESSAGE_KEY.ENCRYPTION_KEY
        this.init$ = this.upgradeLegacyEncryption(this.init$);
        this.init$ = this.attachUserMasterServiceInit(this.init$); // INIT_MESSAGE_KEY.USER_MASTER_DATA
    }

    /**
     * Make sure the odata consuming services are initialized in the correct order
     *
     * @param obs$ the observable to attach the service initialization to
     * @returns the observable with the service initialization
     */
    public attachServiceInit(obs$: Observable<unknown>): Observable<void> {
        let startTimer: moment.Moment;
        return obs$.pipe(
            catchError((error) => {
                if (this.errorCanBeHandled(error)) {
                    return of(undefined);
                }

                return throwError(() => error);
            }),
            tap({
                next: () => (startTimer = moment()),
                error: () => (startTimer = moment()),
            }),
            mergeMap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.SERVICE_INIT,
                        INIT_MESSAGE_STATE.PENDING,
                        of(`Initializing odata consuming services`),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.SERVICE_INIT,
                        INIT_MESSAGE_STATE.PENDING,
                        of(`Initializing ConfigService, ValueHelperService`),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return forkJoin([this.configService.init(), this.valueHelperService.init()]);
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }
                this.log.warn(
                    `InitializationService: attachServiceInit: Error while initializing services: ${JSON.stringify(
                        error,
                        Object.getOwnPropertyNames(error),
                    )}`,
                );
                return of(undefined);
            }),
            tap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.SERVICE_INIT,
                        INIT_MESSAGE_STATE.SUCCESS,
                        of(`Initializing ConfigService, ValueHelperService`),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                startTimer = moment();
            }),
            mergeMap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.SERVICE_INIT,
                        INIT_MESSAGE_STATE.PENDING,
                        of(`Initializing MeasurementToolsService`),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return this.measurementToolsService.init();
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }
                this.log.warn(
                    `InitializationService: attachServiceInit: Error while initializing MeasurementToolsService: ${JSON.stringify(
                        error,
                        Object.getOwnPropertyNames(error),
                    )}`,
                );
                return of(undefined);
            }),
            map(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.SERVICE_INIT,
                        INIT_MESSAGE_STATE.SUCCESS,
                        of(`Initializing MeasurementToolsService`),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return undefined;
            }),
        );
    }

    // Function to load any effects that should happen after initialization is completed.
    public executeRemainingSideEffects(): void {
        this.formsService.restoreLastOpenedForm();
    }

    private attachEncryptionServiceInit(obs$: Observable<unknown>): Observable<void> {
        this.initMessagesSubject.next(
            new InitMessage(
                INIT_MESSAGE_KEY.ENCRYPTION_KEY,
                INIT_MESSAGE_STATE.ADDED,
                this.translate.stream('APP_START_INFO_ENCRYPTION'),
                of(`0s`),
            ),
        );
        let startTimer: moment.Moment;
        return obs$.pipe(
            tap(
                () => (startTimer = moment()),
                () => (startTimer = moment()),
            ),
            tap(() =>
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.ENCRYPTION_KEY,
                        INIT_MESSAGE_STATE.PENDING,
                        this.translate.stream('APP_START_INFO_ENCRYPTION'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                ),
            ),
            mergeMap(() => this.encryptionInitializer.init()),
            mergeMap(() => {
                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.ENCRYPTION_KEY,
                        INIT_MESSAGE_STATE.SUCCESS,
                        this.translate.stream('APP_START_INFO_ENCRYPTION'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return of(undefined);
            }),
            catchError((error) => {
                if (error instanceof InitError) {
                    return throwError(() => error);
                }

                this.initMessagesSubject.next(
                    new InitMessage(
                        INIT_MESSAGE_KEY.ENCRYPTION_KEY,
                        INIT_MESSAGE_STATE.ERROR,
                        this.translate.stream('APP_START_ERROR_ENCRYPTION_FAILED'),
                        of(`${moment().diff(startTimer, 'seconds')}s`),
                    ),
                );
                return throwError(() => new InitError(<Error>error));
            }),
            finalize(() =>
                this.log.debug(
                    `InitializationService: attachEncryptionServiceInit: time taken: ${
                        moment().valueOf() - startTimer.valueOf()
                    } ms`,
                ),
            ),
        );
    }
}
